// Rechnungsdetail — Split-View (Candis/Lexoffice-Muster): links die menschenlesbare
// Darstellung des KANONISCHEN Modells (das XML ist die Wahrheit — nie Roh-XML als
// Standardansicht), rechts Status-Timeline (Stampli: alles am Beleg, unveraenderlich),
// Validierung, Matching und die Aktionsleiste. Ablehnung erfordert einen Kommentar.

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Download, CheckCircle2, XCircle, Database, FileCode2, RefreshCw, Send } from 'lucide-react';
import { Card, SectionTitle, BusinessChip, TransportChip, FormatBadge, money, fmtDate, inputCls, btnPrimary, btnGhost } from './ui';
import { getInvoice, approveInvoice, rejectInvoice, postToSap, rematch, generateInvoice, markSent, downloadFile, fileBlobUrl, setPlant } from '../api';

const InvoiceDetail = ({ user, isDarkMode, t, invoiceId, formats, regimes, plants = [], sapConnected = null, onBack, showSuccess, showError }) => {
  const [inv, setInv] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [genFormat, setGenFormat] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(true);

  const load = useCallback(async () => {
    try { setInv(await getInvoice(user, invoiceId)); }
    catch (e) { showError?.(e.message); }
  }, [user, invoiceId, showError]);
  useEffect(() => { load(); }, [load]);

  // PDF-Vorschau: erste PDF-Datei (ZUGFeRD-Original bei Eingang, Hybrid-Umschlag bei
  // Ausgang) als Blob-URL laden — iframe kann den JWT-Header nicht selbst mitschicken.
  const pdfFileId = ((inv && inv.files) || []).find((f) => f.mime === 'application/pdf' || /\.pdf$/i.test(f.filename || ''))?.id ?? null;
  useEffect(() => {
    if (!pdfFileId) { setPdfUrl(null); return undefined; }
    let alive = true; let url = null;
    fileBlobUrl(user, invoiceId, pdfFileId)
      .then((u) => { if (alive) { url = u; setPdfUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (alive) setPdfUrl(null); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [user, invoiceId, pdfFileId]);

  if (!inv) return <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('einvoicing.loading')}</p>;

  const c = inv.canonical || {};
  const regime = (regimes || []).find((r) => r.key === inv.regime);
  const textMain = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const textMuted = isDarkMode ? 'text-white/60' : 'text-slate-500';
  const rowBorder = isDarkMode ? 'border-white/[0.08]' : 'border-slate-100';

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.ok === false) showError?.(res.error);
      else if (okMsg) showSuccess?.(okMsg);
      await load();
    } catch (e) { showError?.(e.message); }
    finally { setBusy(false); }
  };

  const lastValidation = [...(inv.events || [])].reverse().find((e) => e.event.startsWith('validation_'));
  const lastMatch = [...(inv.events || [])].reverse().find((e) => e.event === 'matched');
  const outboundFormats = regime ? regime.formats.outbound : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <h2 className={`text-lg font-bold ${textMain}`}>
          {inv.invoice_number || t('einvoicing.noNumber')} · {inv.counterparty_name}
        </h2>
        <BusinessChip state={inv.state} isDarkMode={isDarkMode} t={t} />
        <TransportChip state={inv.transport_state} isDarkMode={isDarkMode} t={t} />
        <FormatBadge format={inv.source_format} isDarkMode={isDarkMode} />
        {regime && <span className={`text-xs ${textMuted}`}>{regime.flag} {regime.country}{!regime.verified && ' ⚠'}</span>}
        {inv.plant && <span className={`text-xs font-mono ${textMuted}`}>· {t('einvoicing.plant')} {inv.plant}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* LINKS: gerenderte Rechnung (3/5) */}
        <div className="lg:col-span-3 space-y-4">
          <Card isDarkMode={isDarkMode}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 ${textMuted}`}>{t('einvoicing.seller')}</div>
                <div className={`text-sm font-medium ${textMain}`}>{c.seller?.name}</div>
                <div className={`text-xs ${textMuted}`}>{c.seller?.address?.street}, {c.seller?.address?.postCode} {c.seller?.address?.city}, {c.seller?.address?.countryCode}</div>
                <div className={`text-xs ${textMuted}`}>{!c.seller?.vatId && c.seller?.taxNumber
                  ? `${t('einvoicing.taxNumber')}: ${c.seller.taxNumber}`
                  : `${t('einvoicing.vatId')}: ${c.seller?.vatId || '—'}`}</div>
              </div>
              <div>
                <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1 ${textMuted}`}>{t('einvoicing.buyer')}</div>
                <div className={`text-sm font-medium ${textMain}`}>{c.buyer?.name}</div>
                <div className={`text-xs ${textMuted}`}>{c.buyer?.address?.street}, {c.buyer?.address?.postCode} {c.buyer?.address?.city}, {c.buyer?.address?.countryCode}</div>
                <div className={`text-xs ${textMuted}`}>{t('einvoicing.vatId')}: {c.buyer?.vatId || '—'}</div>
              </div>
            </div>
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 pb-3 border-b ${rowBorder}`}>
              {[
                [t('einvoicing.colIssueDate'), fmtDate(c.issueDate)],
                [t('einvoicing.colDueDate'), fmtDate(c.dueDate)],
                [t('einvoicing.orderRef'), c.orderReference || '—'],
                [t('einvoicing.buyerRef'), c.buyerReference || '—'],
              ].map(([label, val]) => (
                <div key={label}>
                  <div className={`text-[11px] ${textMuted}`}>{label}</div>
                  <div className={`text-sm ${textMain}`}>{val}</div>
                </div>
              ))}
            </div>

            {/* Positionen — Desktop: Tabelle; Mobil: gestapelte Karten, damit nichts ueber den
                rechten Rand laeuft (kein horizontales Scrollen auf dem Smartphone). */}
            <div className="hidden md:block">
            <table className="w-full mt-3">
              <thead>
                <tr>
                  {[t('einvoicing.linePos'), t('einvoicing.lineName'), t('einvoicing.lineQty'), t('einvoicing.linePrice'), t('einvoicing.lineVat'), t('einvoicing.lineTotal')].map((h, i) => (
                    <th key={h} className={`text-[11px] uppercase tracking-wide font-semibold py-1.5 ${i >= 2 ? 'text-right' : 'text-left'} ${textMuted}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(c.lines || []).map((l, i) => (
                  <tr key={i} className={`border-t ${rowBorder}`}>
                    <td className={`py-2 text-sm ${textMuted}`}>{l.id || i + 1}</td>
                    <td className={`py-2 text-sm ${textMain}`}>{l.name}</td>
                    <td className={`py-2 text-sm text-right tabular-nums ${textMain}`}>{l.quantity} {l.unitCode}</td>
                    <td className={`py-2 text-sm text-right tabular-nums ${textMain}`}>{money(l.netPrice, c.currency)}</td>
                    <td className={`py-2 text-sm text-right tabular-nums ${textMuted}`}>{l.vatRate}%</td>
                    <td className={`py-2 text-sm text-right tabular-nums font-medium ${textMain}`}>{money(l.lineNetAmount, c.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Positionen als gestapelte Karten (mobil) — Nummer+Name oben, Menge×Preis·USt links,
                Positionsbetrag rechts hervorgehoben; nichts laeuft ueber den Rand. */}
            <div className="md:hidden space-y-2 mt-3">
              {(c.lines || []).map((l, i) => (
                <div key={i} className={`rounded-lg p-3 border ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex gap-2">
                    <span className={`text-xs flex-shrink-0 ${textMuted}`}>{l.id || i + 1}</span>
                    <span className={`text-sm font-medium ${textMain}`}>{l.name}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 mt-2">
                    <span className={`text-xs min-w-0 ${textMuted}`}>{l.quantity} {l.unitCode} × {money(l.netPrice, c.currency)} · {l.vatRate}%</span>
                    <span className={`flex-shrink-0 text-sm font-semibold tabular-nums whitespace-nowrap ${textMain}`}>{money(l.lineNetAmount, c.currency)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Summenblock */}
            <div className="flex justify-end mt-3">
              <div className="w-full max-w-xs sm:w-64 space-y-1">
                {[
                  [t('einvoicing.totalNet'), c.totals?.taxExclusive],
                  [t('einvoicing.totalVat'), c.totals?.taxAmount],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className={textMuted}>{label}</span>
                    <span className={`tabular-nums ${textMain}`}>{money(val, c.currency)}</span>
                  </div>
                ))}
                <div className={`flex justify-between text-base font-bold pt-1 border-t ${rowBorder}`}>
                  <span className={textMain}>{t('einvoicing.totalPayable')}</span>
                  <span className={`tabular-nums ${textMain}`}>{money(c.totals?.payable, c.currency)}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Dateien (Original = rechtliches Artefakt) */}
          <Card isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.files')}</SectionTitle>
            {(inv.files || []).map((f) => (
              <div key={f.id} className={`flex items-center justify-between py-2 border-b last:border-0 ${rowBorder}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileCode2 className={`w-4 h-4 flex-shrink-0 ${textMuted}`} />
                  <div className="min-w-0">
                    <div className={`text-sm truncate ${textMain}`}>{f.filename}</div>
                    <div className={`text-[11px] ${textMuted}`}>
                      {t(`einvoicing.fileKind.${f.kind}`)} · {(f.size_bytes / 1024).toFixed(1)} KB
                      {f.retention_until ? ` · ${t('einvoicing.retentionUntil')} ${fmtDate(f.retention_until)}` : ''}
                    </div>
                  </div>
                </div>
                <button className={btnGhost(isDarkMode)} onClick={() => downloadFile(user, inv.id, f.id, f.filename).catch((e) => showError?.(e.message))}>
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </Card>

          {/* PDF-Vorschau (Sichtkopie — massgeblich ist das XML) */}
          {pdfUrl && (
            <Card isDarkMode={isDarkMode}>
              <div className="flex items-center justify-between gap-2">
                <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.pdfView')}</SectionTitle>
                <button className={btnGhost(isDarkMode)} onClick={() => setPdfOpen((o) => !o)}>
                  {pdfOpen ? t('einvoicing.pdfHide') : t('einvoicing.pdfShow')}
                </button>
              </div>
              {pdfOpen && (
                <>
                  <iframe
                    title={t('einvoicing.pdfView')}
                    src={pdfUrl}
                    className={`w-full mt-2 rounded-xl border ${rowBorder}`}
                    style={{ height: 560 }}
                  />
                  <p className={`mt-2 text-[11px] ${textMuted}`}>{t('einvoicing.pdfViewNote')}</p>
                </>
              )}
            </Card>
          )}
        </div>

        {/* RECHTS: Status, Validierung, Matching, Aktionen (2/5) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Aktionsleiste */}
          <Card isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.actions')}</SectionTitle>
            <div className="space-y-2">
              {/* Werkszuordnung — manuelles Setzen/Aendern (z. B. wenn kein BANF-Match vorlag) */}
              {plants.length > 0 && (
                <div>
                  <label className={`text-xs ${textMuted}`}>{t('einvoicing.plant')}</label>
                  <select
                    value={inv.plant || ''}
                    disabled={busy}
                    onChange={(e) => act(() => setPlant(user, inv.id, e.target.value), t('einvoicing.plantSetMsg'))}
                    className={`${inputCls(isDarkMode)} w-full mt-1`}
                  >
                    <option value="">{t('einvoicing.plantUnassigned')}</option>
                    {plants.map((p) => <option key={p.plant} value={p.plant}>{p.plant} · {p.name}</option>)}
                  </select>
                </div>
              )}
              {inv.direction === 'inbound' && inv.state === 'in_approval' && (
                <>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder={t('einvoicing.commentPlaceholder')} rows={2}
                    className={`${inputCls(isDarkMode)} w-full resize-none`} />
                  <div className="flex gap-2">
                    <button disabled={busy} className={`${btnPrimary(isDarkMode)} flex-1`}
                      onClick={() => act(() => approveInvoice(user, inv.id, comment), t('einvoicing.approvedMsg'))}>
                      <span className="flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" />{t('einvoicing.approve')}</span>
                    </button>
                    <button disabled={busy} className={`${btnGhost(isDarkMode)} flex-1`}
                      onClick={() => {
                        if (!comment.trim()) { showError?.(t('einvoicing.rejectNeedsComment')); return; }
                        act(() => rejectInvoice(user, inv.id, comment), t('einvoicing.rejectedMsg'));
                      }}>
                      <span className="flex items-center justify-center gap-2"><XCircle className="w-4 h-4" />{t('einvoicing.reject')}</span>
                    </button>
                  </div>
                </>
              )}
              {sapConnected !== false && inv.direction === 'inbound' && inv.state === 'approved' && (
                <button disabled={busy} className={`${btnPrimary(isDarkMode)} w-full`}
                  onClick={() => act(() => postToSap(user, inv.id), t('einvoicing.postedMsg'))}>
                  <span className="flex items-center justify-center gap-2"><Database className="w-4 h-4" />{t('einvoicing.postToSap')}</span>
                </button>
              )}
              {inv.direction === 'outbound' && (inv.state === 'draft' || inv.state === 'generated') && (
                <>
                  <div className="flex gap-2">
                    <select value={genFormat || outboundFormats[0] || ''} onChange={(e) => setGenFormat(e.target.value)} className={`${inputCls(isDarkMode)} flex-1`}>
                      {outboundFormats.map((f) => <option key={f} value={f}>{(formats || []).find((x) => x.id === f)?.name || f}</option>)}
                    </select>
                    <button disabled={busy} className={btnPrimary(isDarkMode)}
                      onClick={() => act(() => generateInvoice(user, inv.id, genFormat || outboundFormats[0]), t('einvoicing.generatedMsg'))}>
                      {t('einvoicing.generate')}
                    </button>
                  </div>
                  {inv.state === 'generated' && (
                    <button disabled={busy} className={`${btnGhost(isDarkMode)} w-full`}
                      onClick={() => act(() => markSent(user, inv.id, 'download'), t('einvoicing.sentMsg'))}>
                      <span className="flex items-center justify-center gap-2"><Send className="w-4 h-4" />{t('einvoicing.markSent')}</span>
                    </button>
                  )}
                </>
              )}
              {inv.sap_document_no && (
                <div className={`text-xs ${textMuted}`}>{t('einvoicing.sapDocNo')}: <span className="font-mono">{inv.sap_document_no}</span></div>
              )}
            </div>
          </Card>

          {/* Validierung */}
          {lastValidation && (
            <Card isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}
                right={<button onClick={() => act(() => rematch(user, inv.id))} className={btnGhost(isDarkMode)} title={t('einvoicing.rematch')}><RefreshCw className="w-3.5 h-3.5" /></button>}>
                {t('einvoicing.validation')}
              </SectionTitle>
              {(lastValidation.detail?.errors || []).length === 0
                ? <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>✓ {t('einvoicing.validationOk')} <span className={`text-xs ${textMuted}`}>({t('einvoicing.validationCoreNote')})</span></p>
                : (lastValidation.detail?.errors || []).map((e, i) => (
                  <p key={i} className={`text-xs mb-1 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}><span className="font-mono">{e.rule}</span> — {e.message}</p>
                ))}
              {(lastValidation.detail?.warnings || []).map((w, i) => (
                <p key={i} className={`text-xs mb-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}><span className="font-mono">{w.rule}</span> — {w.message}</p>
              ))}
            </Card>
          )}

          {/* Matching */}
          {inv.direction === 'inbound' && lastMatch && (
            <Card isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.matching')}</SectionTitle>
              <p className={`text-sm mb-1 ${textMain}`}>
                {t(`einvoicing.match.${lastMatch.detail?.status}`)}
                {lastMatch.detail?.prId ? ` — BANF-${lastMatch.detail.prId}` : ''}
                {lastMatch.detail?.score ? ` (Score ${lastMatch.detail.score})` : ''}
              </p>
              {(lastMatch.detail?.deltas || []).map((d, i) => (
                <p key={i} className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>• {d}</p>
              ))}
            </Card>
          )}

          {/* Timeline (unveraenderliches Audit-Log) */}
          <Card isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.timeline')}</SectionTitle>
            <div className="space-y-0">
              {[...(inv.events || [])].reverse().map((e) => (
                <div key={e.id} className={`flex gap-3 py-2 border-b last:border-0 ${rowBorder}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${e.event.includes('failed') || e.event === 'rejected' ? 'bg-red-500' : 'bg-violet-500'}`} />
                  <div className="min-w-0">
                    <div className={`text-sm ${textMain}`}>{t(`einvoicing.event.${e.event}`)}</div>
                    <div className={`text-[11px] ${textMuted}`}>{fmtDate(e.created_at)} · {e.actor}
                      {e.detail?.comment ? ` · „${e.detail.comment}"` : ''}
                      {e.detail?.note ? ` · ${e.detail.note}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
